<template>
  <AppMainDrawer>
    <template #drawer>
      <DrawerStyled v-if="pageHasFilters[route.name]">
        <template #drawer-content>
          <ExpandableFilters />
        </template>
      </DrawerStyled>
    </template>

    <template #content>
      <div class="job-architecture-page">
        <TopBarForDrawer>
          <template #page-title>
            <TopBarForDrawerTitle
              :page-title="route.meta.title"
              :page-description="pageDescription"
              :is-loading="pageLoading"
            />
          </template>
          <template #summaries>
            <div
              v-if="!pageLoading"
              class="job-architecture-summaries"
            >
              <h3 class="text-body-2 text-textDarkest">
                Groups
                <span class="job-architecture-summary-value">
                  {{ numberWithCommas(architectureTotals.groups) }}
                </span>
              </h3>
              <h3 class="text-body-2 text-textDarkest">
                Families
                <span class="job-architecture-summary-value">
                  {{ numberWithCommas(architectureTotals.families) }}
                </span>
              </h3>
              <h3 class="text-body-2 text-textDarkest">
                Job Profiles
                <span class="job-architecture-summary-value">
                  {{ numberWithCommas(architectureTotals.profiles) }}
                </span>
              </h3>
            </div>
          </template>
          <template #topbar-actions>
            <div class="job-architecture-topbar-actions">
              <FiltersDrawerToggle
                v-if="pageHasFilters[route.name]"
                v-model="open"
                :is-loading="pageLoading"
                :are-filters-applied="areNonIgnorableFiltersApplied"
              />
              <JobArchitectureSearch />
              <v-btn
                v-if="selectedProfileUlids.length"
                color="brandPrimary"
                variant="flat"
                class="compare-selected-btn text-button-2"
                @click="openCompare"
              >
                Compare Selected ({{ selectedProfileUlids.length }})
              </v-btn>
              <v-btn
                v-if="selectedProfileUlids.length"
                variant="text"
                color="brandPrimary"
                class="text-button-3 compare-clear-btn"
                @click="selectedProfileUlids = []"
              >
                Clear
              </v-btn>
            </div>
          </template>
        </TopBarForDrawer>

        <div class="job-architecture-list-wrapper">
          <JobArchitectureListView
            v-model:selected-profiles="selectedProfileUlids"
            :tree-data="filteredTreeData"
          />
        </div>
      </div>
    </template>
  </AppMainDrawer>
</template>

<script setup>
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import {
  useJobArchitectureFilters,
  mergeJobArchitectureRouteQuery,
} from '@/composables/useJobArchitectureFilters';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useAppLoading } from '@/stores/app/loading';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';
import { numberWithCommas } from '@/helpers/formatters';
import AppMainDrawer from '@/components/shared/AppMainDrawer.vue';
import DrawerStyled from '@/components/shared/DrawerStyled.vue';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import ExpandableFilters from '@/components/filter/ExpandableFilters.vue';
import FiltersDrawerToggle from '@/components/filter/FiltersDrawerToggle.vue';
import JobArchitectureListView from '@/components/jobArchitecture/JobArchitectureListView.vue';
import JobArchitectureSearch from '@/components/jobArchitecture/JobArchitectureSearch.vue';

const route = useRoute();
const router = useRouter();

const selectedProfileUlids = ref([]);

const openCompare = () => {
  if (!selectedProfileUlids.value.length) return;

  router.push({
    name: 'job-architecture-compare',
    query: { profiles: selectedProfileUlids.value.join(',') },
  });
};

const pageDescription = computed(() => pageDescriptions()[route.name] || '');

const { areNonIgnorableFiltersApplied, mergedTempFilters } = storeToRefs(useFilters());
const { pageHasFilters } = storeToRefs(useAppBase());
const { pageLoading } = storeToRefs(useAppLoading());
const { open } = storeToRefs(useFilterDrawer());

const { setFilterConfig, clearPersistentFilters } = useFilters();
const { showFilterBarSettings } = useAppBase();
const { clearSelection, loadTree } = useJobArchitecture();

const {
  initializeFilters,
  syncAppliedState,
  filteredTreeData,
  clearSearch,
  refreshFilterOptions,
  searchText,
} = useJobArchitectureFilters();

const architectureTotals = computed(() => {
  const groups = filteredTreeData.value?.groups || [];
  const families = groups.flatMap((group) => group.families || []);
  const profiles = families.flatMap((family) => family.profiles || []);

  return {
    groups: groups.length,
    families: families.length,
    profiles: profiles.length,
  };
});

watch(() => searchText.value, () => {
  refreshFilterOptions();
});

const loadPage = () => {
  syncAppliedState();

  router.push({
    query: mergeJobArchitectureRouteQuery(route.query, mergedTempFilters.value),
  }).catch(() => true);
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobArchitecture',
    showMyJobs: false,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  clearSelection();

  await loadTree();

  const familyUlid = route.query.family;

  if (familyUlid) {
    const { family: _drop, ...queryRest } = route.query;

    router.replace({
      name: 'job-architecture-family',
      params: { familyUlid },
      query: queryRest,
    });

    return;
  }

  initializeFilters(route.query);
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates();

  clearPersistentFilters();

  clearSearch();

  setFilterConfig({
    subMenuType: null,
  });
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-summaries {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  margin-left: 12px;
}

.job-architecture-summary-value {
  color: @text-brand;
}

.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px 16px;
}

.job-architecture-list-wrapper {
  flex-shrink: 0;
  border: 1px solid @stroke-default;
  border-top: none;
  border-radius: 8px;
  background: @bg-default;
}

:deep(.topbar) {
  padding-left: 0px;
  padding-right: 0px;
}

.job-architecture-page :deep(.topbar-actions) {
  width: 100%;
  min-height: 40px;
}

.job-architecture-topbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  min-height: 40px;
  gap: 8px;
  row-gap: 10px;
}

.compare-selected-btn {
  margin-left: auto;
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 12px;
  border-radius: 8px !important;
}

.compare-clear-btn {
  padding: 0 6px !important;
  min-width: 0 !important;
  height: auto !important;
  min-height: auto !important;
}
</style>
